import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  posProductsApi,
  type PosProductRow,
  type UpsertPosProductPayload,
} from "../../api/posProductsApi";
import { useAuth } from "../../auth/AuthContext";
import { canAccessPosMonitor } from "../../auth/roles";

type Draft = {
  code: string;
  name: string;
  unitPrice: string;
  stockQty: string;
  vatRate: string;
  ean: string;
  categoryCode: string;
  description: string;
  isWeighable: boolean;
  isActive: boolean;
};

const emptyDraft = (): Draft => ({
  code: "",
  name: "",
  unitPrice: "0",
  stockQty: "",
  vatRate: "22",
  ean: "",
  categoryCode: "",
  description: "",
  isWeighable: false,
  isActive: true,
});

function num(value: string | number | null | undefined) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function toPayload(draft: Draft): UpsertPosProductPayload {
  return {
    code: draft.code.trim().toUpperCase(),
    name: draft.name.trim(),
    unitPrice: num(draft.unitPrice),
    stockQty: draft.stockQty.trim() === "" ? undefined : num(draft.stockQty),
    vatRate: draft.vatRate.trim() === "" ? undefined : num(draft.vatRate),
    ean: draft.ean.trim() || undefined,
    categoryCode: draft.categoryCode.trim().toUpperCase() || undefined,
    description: draft.description.trim() || undefined,
    isWeighable: draft.isWeighable,
    isActive: draft.isActive,
    listPrice: num(draft.unitPrice),
  };
}

export default function PosProductsPage() {
  const { token, user } = useAuth();
  const canView = canAccessPosMonitor(user?.role);
  const canEdit = user?.role === "ADMIN" || user?.role === "MANAGER";

  const [products, setProducts] = useState<PosProductRow[]>([]);
  const [categories, setCategories] = useState<Array<{ code: string; name: string }>>([]);
  const [query, setQuery] = useState("");
  const [categoryCode, setCategoryCode] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [csvText, setCsvText] = useState("");

  const load = useCallback(async () => {
    if (!token || !canView) return;
    setLoading(true);
    setError("");
    try {
      const [prods, cats] = await Promise.all([
        posProductsApi.list(token, {
          query: query.trim() || undefined,
          categoryCode: categoryCode || undefined,
          includeInactive,
          limit: 3000,
        }),
        posProductsApi.listCategories(token),
      ]);
      setProducts(prods || []);
      setCategories(cats || []);
    } catch (e: any) {
      setError(e?.message || "Errore caricamento prodotti");
    } finally {
      setLoading(false);
    }
  }, [token, canView, query, categoryCode, includeInactive]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredHint = useMemo(() => `${products.length} articoli`, [products.length]);

  function startCreate() {
    setEditingCode(null);
    setDraft(emptyDraft());
    setMessage("");
    setError("");
  }

  function startEdit(p: PosProductRow) {
    setEditingCode(p.code);
    setDraft({
      code: p.code,
      name: p.name,
      unitPrice: String(num(p.unitPrice)),
      stockQty: p.stockQty == null || p.stockQty === "" ? "" : String(num(p.stockQty)),
      vatRate: p.vatRate == null || p.vatRate === "" ? "" : String(num(p.vatRate)),
      ean: p.ean || "",
      categoryCode: p.category?.code || "",
      description: p.description || "",
      isWeighable: Boolean(p.isWeighable),
      isActive: p.isActive !== false,
    });
    setMessage("");
    setError("");
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!token || !canEdit) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const res = await posProductsApi.upsert(token, toPayload(draft));
      setMessage(`Salvato ${res.product.code} — ${res.product.name}`);
      setEditingCode(res.product.code);
      await load();
    } catch (err: any) {
      setError(err?.message || "Salvataggio fallito");
    } finally {
      setBusy(false);
    }
  }

  async function handleDeactivate(code: string) {
    if (!token || !canEdit) return;
    if (!window.confirm(`Disattivare l'articolo ${code}?`)) return;
    setBusy(true);
    setError("");
    try {
      await posProductsApi.deactivate(token, code);
      setMessage(`Articolo ${code} disattivato`);
      if (editingCode === code) startCreate();
      await load();
    } catch (err: any) {
      setError(err?.message || "Disattivazione fallita");
    } finally {
      setBusy(false);
    }
  }

  async function handleImportCsv(dryRun: boolean) {
    if (!token || !canEdit) return;
    if (!csvText.trim()) {
      setError("Incolla o carica un CSV");
      return;
    }
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const res = await posProductsApi.importCsv(token, csvText, dryRun);
      setMessage(
        dryRun
          ? `Anteprima CSV: ${res.prepared} righe valide`
          : `Import CSV completato: ${res.synced ?? res.prepared} prodotti`,
      );
      if (!dryRun) {
        setCsvText("");
        await load();
      }
    } catch (err: any) {
      setError(err?.message || "Import CSV fallito");
    } finally {
      setBusy(false);
    }
  }

  async function handleExportPlu(onlyWeighable: boolean) {
    if (!token) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const data = await posProductsApi.downloadPlu(token, onlyWeighable);
      const blob = new Blob([data.content || ""], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename || "plu_corevian.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setMessage(
        `PLU esportato: ${data.count} articoli. Copia il file sulla cassa (es. C:\\Alenia\\Asciiplu.asr) o caricalo da NATPOS.`,
      );
    } catch (err: any) {
      setError(err?.message || "Export PLU fallito");
    } finally {
      setBusy(false);
    }
  }

  function onCsvFile(file?: File | null) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ""));
    reader.readAsText(file);
  }

  if (!canView) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-warning mb-0">Accesso riservato a Manager o Admin.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h1 className="h4 mb-1">Prodotti cassa</h1>
          <p className="text-muted small mb-0">
            Gestisci articoli Corevian per NATPOS: nome, prezzo, quantità, nuovi prodotti, import CSV e export PLU bilance.
          </p>
        </div>
        <div className="d-flex gap-2">
          <Link to="/pos/catalog" className="btn btn-sm btn-outline-secondary">
            Sync catalogo
          </Link>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => void load()} disabled={loading}>
            Aggiorna
          </button>
        </div>
      </div>

      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      {message ? <div className="alert alert-success py-2">{message}</div> : null}

      <div className="row g-3">
        <div className="col-12 col-xl-7">
          <div className="card shadow-sm mb-3">
            <div className="card-body">
              <div className="row g-2 align-items-end">
                <div className="col-12 col-md-5">
                  <label className="form-label small mb-1">Cerca</label>
                  <input
                    className="form-control form-control-sm"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Nome / codice / EAN"
                  />
                </div>
                <div className="col-8 col-md-4">
                  <label className="form-label small mb-1">Reparto</label>
                  <select className="form-select form-select-sm" value={categoryCode} onChange={(e) => setCategoryCode(e.target.value)}>
                    <option value="">Tutti</option>
                    {categories.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code} — {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-4 col-md-3">
                  <div className="form-check mt-4">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="includeInactive"
                      checked={includeInactive}
                      onChange={(e) => setIncludeInactive(e.target.checked)}
                    />
                    <label className="form-check-label small" htmlFor="includeInactive">
                      Anche disattivi
                    </label>
                  </div>
                </div>
              </div>
              <div className="small text-muted mt-2">{loading ? "Caricamento..." : filteredHint}</div>
            </div>
          </div>

          <div className="card shadow-sm">
            <div className="table-responsive" style={{ maxHeight: 560 }}>
              <table className="table table-sm table-hover align-middle mb-0">
                <thead className="table-light sticky-top">
                  <tr>
                    <th>Codice</th>
                    <th>Nome</th>
                    <th>Prezzo</th>
                    <th>Qty</th>
                    <th>Reparto</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className={p.isActive === false ? "table-secondary" : undefined}>
                      <td className="small">
                        <code>{p.code}</code>
                        {p.isWeighable ? <span className="badge text-bg-info ms-1">Bilancia</span> : null}
                      </td>
                      <td>{p.name}</td>
                      <td>€ {num(p.unitPrice).toFixed(2)}</td>
                      <td>{p.stockQty == null || p.stockQty === "" ? "—" : num(p.stockQty)}</td>
                      <td className="small">{p.category?.code || "—"}</td>
                      <td className="text-end">
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => startEdit(p)}>
                          Modifica
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!products.length && !loading ? (
                    <tr>
                      <td colSpan={6} className="text-muted text-center py-4">
                        Nessun prodotto. Importa da Easy Retail o crea un articolo.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-5">
          <div className="card shadow-sm mb-3">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h2 className="h6 mb-0">{editingCode ? `Modifica ${editingCode}` : "Nuovo prodotto"}</h2>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={startCreate}>
                  Nuovo
                </button>
              </div>
              <form className="row g-2" onSubmit={handleSave}>
                <div className="col-6">
                  <label className="form-label small mb-1">Codice</label>
                  <input
                    className="form-control form-control-sm"
                    value={draft.code}
                    onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))}
                    required
                    disabled={Boolean(editingCode)}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small mb-1">Prezzo €</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-control form-control-sm"
                    value={draft.unitPrice}
                    onChange={(e) => setDraft((d) => ({ ...d, unitPrice: e.target.value }))}
                    required
                  />
                </div>
                <div className="col-12">
                  <label className="form-label small mb-1">Nome articolo</label>
                  <input
                    className="form-control form-control-sm"
                    value={draft.name}
                    onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                    required
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small mb-1">Quantità / giacenza</label>
                  <input
                    type="number"
                    step="0.001"
                    className="form-control form-control-sm"
                    value={draft.stockQty}
                    onChange={(e) => setDraft((d) => ({ ...d, stockQty: e.target.value }))}
                    placeholder="opzionale"
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small mb-1">IVA %</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control form-control-sm"
                    value={draft.vatRate}
                    onChange={(e) => setDraft((d) => ({ ...d, vatRate: e.target.value }))}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small mb-1">EAN</label>
                  <input
                    className="form-control form-control-sm"
                    value={draft.ean}
                    onChange={(e) => setDraft((d) => ({ ...d, ean: e.target.value }))}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small mb-1">Reparto</label>
                  <input
                    className="form-control form-control-sm"
                    list="pos-category-list"
                    value={draft.categoryCode}
                    onChange={(e) => setDraft((d) => ({ ...d, categoryCode: e.target.value }))}
                    placeholder="es. 6"
                  />
                  <datalist id="pos-category-list">
                    {categories.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </datalist>
                </div>
                <div className="col-12">
                  <label className="form-label small mb-1">Descrizione</label>
                  <textarea
                    className="form-control form-control-sm"
                    rows={2}
                    value={draft.description}
                    onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                  />
                </div>
                <div className="col-6">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isWeighable"
                      checked={draft.isWeighable}
                      onChange={(e) => setDraft((d) => ({ ...d, isWeighable: e.target.checked }))}
                    />
                    <label className="form-check-label small" htmlFor="isWeighable">
                      Articolo bilancia
                    </label>
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isActive"
                      checked={draft.isActive}
                      onChange={(e) => setDraft((d) => ({ ...d, isActive: e.target.checked }))}
                    />
                    <label className="form-check-label small" htmlFor="isActive">
                      Attivo in cassa
                    </label>
                  </div>
                </div>
                <div className="col-12 d-flex gap-2">
                  <button type="submit" className="btn btn-sm btn-primary" disabled={busy || !canEdit}>
                    {busy ? "Salvo..." : "Salva"}
                  </button>
                  {editingCode ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      disabled={busy || !canEdit}
                      onClick={() => void handleDeactivate(editingCode)}
                    >
                      Disattiva
                    </button>
                  ) : null}
                </div>
              </form>
            </div>
          </div>

          <div className="card shadow-sm mb-3">
            <div className="card-body">
              <h2 className="h6">Import CSV prodotti</h2>
              <p className="small text-muted">
                Formato: <code>code;name;unitPrice;stockQty;vatRate;categoryCode;ean;isWeighable</code>
              </p>
              <input
                type="file"
                accept=".csv,text/csv,text/plain"
                className="form-control form-control-sm mb-2"
                onChange={(e) => onCsvFile(e.target.files?.[0])}
                disabled={!canEdit}
              />
              <textarea
                className="form-control form-control-sm mb-2"
                rows={5}
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                placeholder="Incolla CSV..."
                disabled={!canEdit}
              />
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy || !canEdit} onClick={() => void handleImportCsv(true)}>
                  Anteprima
                </button>
                <button type="button" className="btn btn-sm btn-success" disabled={busy || !canEdit} onClick={() => void handleImportCsv(false)}>
                  Importa in casse
                </button>
              </div>
            </div>
          </div>

          <div className="card shadow-sm">
            <div className="card-body">
              <h2 className="h6">Invio alle bilance (PLU)</h2>
              <p className="small text-muted mb-2">
                Scarica il file articoli da caricare sul PC cassa (Alenia/WAAGE). Poi in NATPOS usa il percorso PLU
                (es. <code>C:\Alenia\Asciiplu.asr</code>) oppure copia il file nella cartella bilancia.
              </p>
              <div className="d-flex flex-wrap gap-2">
                <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={() => void handleExportPlu(false)}>
                  Scarica PLU completo
                </button>
                <button type="button" className="btn btn-sm btn-outline-primary" disabled={busy} onClick={() => void handleExportPlu(true)}>
                  Solo articoli bilancia
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
